import type { AxisSelection } from '@atlas/types'
import { AxisCode, ClassifiedObjectType, type FileType } from '@atlas/types'
import { useQuery } from '@tanstack/react-query'
import type { WorkOrder } from '@/domain/work-order.types'
import { type FileWithTagsDTO, fileService } from '@/services/file.service'
import { useUploadFinal, workOrderFilesKey } from './useWorkOrderFiles'

/**
 * Who the files belong to: the order, by its `order_code`, or a campaign, by its
 * `campaign_code`, for files the order's campaign shares with every order on it.
 */
export interface PackFilesScope {
	orderCode: string
	campaignCode?: string | null
}

/** The free-text attributes a file of the scope carries. */
const attributesOf = ({ orderCode, campaignCode }: PackFilesScope): AxisSelection =>
	campaignCode ? { [AxisCode.CAMPAIGN_CODE]: [campaignCode] } : { [AxisCode.ORDER_CODE]: [orderCode] }

/** Upload-store owner tag for one kind of pack file, so its tiles render in its own drop zone. */
export const packFilesOwner = (scope: PackFilesScope, fileType: FileType): string =>
	`wo-pack:${fileType}:${scope.campaignCode ?? scope.orderCode}`

/**
 * Query key for one kind of pack file. Under the order's {@link workOrderFilesKey}, even for a
 * campaign's files, so an upload from the order or a delete of one of its files reaches it.
 */
const packFilesKey = (scope: PackFilesScope, fileType: FileType) => [
	...workOrderFilesKey(scope.orderCode),
	'pack',
	fileType,
	scope.campaignCode ?? null,
]

/**
 * The files of one `file_type` the PM attached for the designer: the order's own, or, with a
 * campaign code, the campaign's, wherever they were uploaded from. Disabled for a campaign
 * scope with no code.
 */
export const usePackFiles = (scope: PackFilesScope, fileType: FileType, enabled = true) =>
	useQuery<FileWithTagsDTO[]>({
		queryKey: packFilesKey(scope, fileType),
		queryFn: () => fileService.discover({ [AxisCode.FILE_TYPE]: [fileType] }, attributesOf(scope)),
		enabled,
	})

/**
 * Upload a pack file: classified with its `file_type` and the order's tuple, stamped with the
 * order's code (and the campaign's, for a campaign file) and audited against the order.
 */
export const useUploadPackFile = (workOrder: WorkOrder, fileType: FileType, campaignCode: string | null = null) => {
	const scope = { orderCode: workOrder.orderCode, campaignCode }
	return useUploadFinal(workOrder.workOrderId, workOrder.orderCode, workOrder.tuple, {
		extraTags: (fileId) => [
			{ objectType: ClassifiedObjectType.FILE, objectId: fileId, axisCode: AxisCode.FILE_TYPE, valueCode: fileType },
		],
		attributes: campaignCode ? { [AxisCode.CAMPAIGN_CODE]: [campaignCode] } : {},
		owner: packFilesOwner(scope, fileType),
	})
}
